/** Request bodies of single-cell edits, built from the view being edited. */
import type {
  WavePlanDcResponse,
  WavePlanDcSplitInput,
  WavePlanLinesResponse,
  WavePlanSkuWeekInput,
} from '@/api/wavePlans'
import { autoFill, samePercent, toStoredPercents } from './model'

/**
 * A SKU's full set of visible weeks with one week set to ``percent``: the API
 * replaces the whole distribution (weeks not sent become 0). Cells the typed
 * value decides on its own are filled in (``autoFill``); the others keep their
 * input: a quantity override keeps its quantity, and percentages go back at the
 * stored precision (4 decimals) with their total kept (a Core SKU's 72.222222%
 * would otherwise be rejected, and rounding each one alone could leave the SKU
 * short of 100%). Retyping a cell's own value keeps its input as it is (a
 * quantity stays a quantity).
 */
export function skuWeeksWithEdit(
  view: WavePlanLinesResponse,
  lineRefId: string,
  weekId: string,
  percent: number
): WavePlanSkuWeekInput[] {
  const line = view.lines.find(item => item.line_ref_id === lineRefId)
  if (!line) throw new Error('This SKU is no longer in the plan.')
  const cells = new Map(line.cells.map(cell => [cell.week_id, cell]))
  const isQuantity = (id: string) =>
    cells.get(id)?.source === 'OVERRIDE_QUANTITY'
  const edited = cells.get(weekId)
  const keepEdited =
    !!edited && isQuantity(weekId) && samePercent(percent, edited.percent)
  const filled =
    autoFill(
      view.weeks.map(week => ({
        weekId: week.id,
        percent: cells.get(week.id)?.percent ?? 0,
        quantityOverride: isQuantity(week.id),
      })),
      weekId,
      percent
    ) ?? new Map()
  const kept = view.weeks.filter(
    week => week.id !== weekId && !filled.has(week.id) && !isQuantity(week.id)
  )
  const stored = new Map(
    toStoredPercents(kept.map(week => cells.get(week.id)?.percent ?? 0)).map(
      (value, index) => [kept[index].id, value]
    )
  )
  return view.weeks.map(week => {
    const cell = cells.get(week.id)
    if (week.id === weekId)
      return keepEdited && cell
        ? { week_id: week.id, quantity: cell.qty }
        : { week_id: week.id, percent }
    const fill = filled.get(week.id)
    if (fill !== undefined) return { week_id: week.id, percent: fill }
    return isQuantity(week.id) && cell
      ? { week_id: week.id, quantity: cell.qty }
      : { week_id: week.id, percent: stored.get(week.id) ?? 0 }
  })
}

/** Whether a SKU request would leave the SKU's inputs as they are (nothing to send). */
export function skuRequestUnchanged(
  view: WavePlanLinesResponse,
  lineRefId: string,
  weeks: WavePlanSkuWeekInput[]
): boolean {
  const line = view.lines.find(item => item.line_ref_id === lineRefId)
  if (!line) return false
  const cells = new Map(line.cells.map(cell => [cell.week_id, cell]))
  return weeks.every(week => {
    const cell = cells.get(week.week_id)
    if (!cell) return false
    const quantity = cell.source === 'OVERRIDE_QUANTITY'
    return week.quantity != null
      ? quantity && cell.qty === week.quantity
      : !quantity && samePercent(week.percent ?? 0, cell.percent)
  })
}

/**
 * A SKU-week's full DC split with one DC set to ``percent``. Virtual routing
 * placeholders ("Single DC Strategy") are never sent: they are not real DCs, and
 * a split on real DCs replaces them. The other DCs keep their input: a quantity
 * stays a quantity, percentages go back at the stored precision with their
 * total kept.
 */
export function dcSplitWithEdit(
  view: WavePlanDcResponse,
  lineRefId: string,
  dcId: string,
  percent: number
): WavePlanDcSplitInput[] {
  const row = view.rows.find(item => item.line_ref_id === lineRefId)
  if (!row) throw new Error('This SKU has no allocation in the selected wave.')
  const cells = row.cells.filter(cell => !cell.virtual)
  const quantity = (cell: (typeof cells)[number]) =>
    cell.source === 'OVERRIDE_QUANTITY'
  const percents = cells.filter(cell => cell.dc_id !== dcId && !quantity(cell))
  const stored = new Map(
    toStoredPercents(percents.map(cell => cell.percent)).map((value, index) => [
      percents[index].dc_id,
      value,
    ])
  )
  const split: WavePlanDcSplitInput[] = cells.map(cell =>
    cell.dc_id === dcId
      ? { dc_id: cell.dc_id, percent }
      : quantity(cell)
        ? { dc_id: cell.dc_id, quantity: cell.raw }
        : { dc_id: cell.dc_id, percent: stored.get(cell.dc_id) ?? 0 }
  )
  return cells.some(cell => cell.dc_id === dcId)
    ? split
    : [...split, { dc_id: dcId, percent }]
}
